/**
 * BusinessEditPage — /business/:slug/edit
 *
 * Full profile editor for the current business. Tabbed layout so owners
 * can dive into: Basics, Contact & hours, Social links, Team members,
 * and the AI-fill helper. Only owners and managers see the editor;
 * everyone else gets a 403 message.
 *
 * All writes go through PATCH /api/businesses/{id} except the team
 * management which uses the dedicated /invite, /members endpoints.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CustomPagesPanel from "@/pages/business-edit/CustomPagesPanel";
import ImportWebsitePanel from "@/pages/business-edit/ImportWebsitePanel";
import BusinessMagicLinkInvite from "@/components/BusinessMagicLinkInvite";

// Small preview card reused by AiFillPanel — extracted verbatim from the
// old inline definition after the CustomPages / ImportWebsite refactor.
function PreviewCard({ title, body }) {
  if (!body) return null;
  return (
    <div className="wofy-card !p-4">
      <div className="overline mb-1.5">{title}</div>
      <p className="text-sm whitespace-pre-line leading-relaxed">{body}</p>
    </div>
  );
}

import {
  Building2, Save, Loader2, MapPin, Phone, Mail, Globe, Clock,
  Users, UserPlus, Sparkles, ArrowLeft, X, ChevronDown, Trash2, ShieldCheck,
  Download, FileText,
} from "lucide-react";

const INDUSTRIES = [
  "restaurant", "hotel", "hospital", "school", "lawyer", "doctor",
  "realtor", "manufacturer", "distributor", "charity", "retail",
  "handyman", "it", "tourism", "education", "construction", "agriculture", "other",
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TABS = [
  { id: "basics",    label: "Basics",         Icon: Building2 },
  { id: "contact",   label: "Contact & hours", Icon: Phone },
  { id: "social",    label: "Social & web",   Icon: Globe },
  { id: "pages",     label: "Custom pages",   Icon: FileText },
  { id: "import",    label: "Import website", Icon: Download },
  { id: "team",      label: "Team",           Icon: Users },
  { id: "ai",        label: "AI-fill",        Icon: Sparkles },
];

export default function BusinessEditPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("basics");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [linkedSite, setLinkedSite] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/businesses/by-slug/${slug}`);
      setBiz({ ...data, hours: data.hours || {}, social: data.social || {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load this business.");
      nav("/businesses");
    } finally { setLoading(false); }
  }, [slug, nav]);

  useEffect(() => { load(); }, [load]);

  // Look up whether this business already has an "instant website" spun off it.
  useEffect(() => {
    if (!biz?.id) return;
    http.get(`/websites/for-source/business/${biz.id}`)
      .then(r => setLinkedSite(r.data?.site || null))
      .catch(() => setLinkedSite(null));
  }, [biz?.id]);

  const isOwner   = useMemo(() => Boolean(biz && user && biz.owner_id === user.id), [biz, user]);
  const canEdit   = isOwner; // Managers also can — biz doc doesn't expose roles publicly, so gate to owner in UI.

  const set = (patch) => { setBiz((b) => ({ ...b, ...patch })); setDirty(true); };
  const setHours = (day, val) => { setBiz((b) => ({ ...b, hours: { ...b.hours, [day]: val } })); setDirty(true); };
  const setSocial = (k, val) => { setBiz((b) => ({ ...b, social: { ...b.social, [k]: val } })); setDirty(true); };

  const save = async () => {
    if (!biz?.id) return;
    setSaving(true);
    try {
      const payload = {
        name: biz.name, description: biz.description, industry: biz.industry,
        founded_year: biz.founded_year ? Number(biz.founded_year) : null,
        contact_person: biz.contact_person, contact_email: biz.contact_email || null,
        contact_phone: biz.contact_phone, address: biz.address,
        city: biz.city, state: biz.state, country: biz.country, postal_code: biz.postal_code,
        website: biz.website, hours: biz.hours, social: biz.social,
        logo_url: biz.logo_url, hero_image_url: biz.hero_image_url,
        is_public: biz.is_public !== false,
      };
      // Strip null/empty strings so PATCH doesn't overwrite with blanks.
      const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined && v !== "" && v !== null));
      const { data } = await http.patch(`/businesses/${biz.id}`, clean);
      setBiz({ ...biz, ...(data.business || {}) });
      setDirty(false);
      toast.success("Business saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>;
  if (!canEdit) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <ShieldCheck className="w-10 h-10 mx-auto opacity-40 mb-2" />
        <h1 className="font-display text-2xl mb-2">You can't edit this business</h1>
        <p className="opacity-70 mb-4">Only the owner and managers can access this page.</p>
        <Link to={`/business/${slug}`} className="wofy-btn-ghost inline-flex">← Back to profile</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid={`business-edit-${biz.slug}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={`/business/${biz.slug}`} className="text-xs opacity-70 inline-flex items-center gap-1 mb-1"><ArrowLeft className="w-3 h-3" /> Back to profile</Link>
          <h1 className="font-display text-3xl">Edit {biz.name}</h1>
        </div>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="wofy-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="business-edit-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-6">
        {/* Sidebar tabs */}
        <nav className="space-y-1" data-testid="business-edit-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id} type="button" onClick={() => setTab(id)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors"
              style={{ background: tab === id ? "var(--wofy-primary)" : "transparent", color: tab === id ? "white" : "var(--wofy-text)", fontWeight: tab === id ? 600 : 400 }}
              data-testid={`business-edit-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
          {/* Instant website CTA — visible only to owner */}
          {isOwner && (
            linkedSite ? (
              <div className="mt-4 rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--wofy-border)" }} data-testid="business-edit-website-linked">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">Your website</div>
                <div className="text-sm font-medium truncate">{linkedSite.name}</div>
                <div className="text-[11px] opacity-70">
                  Status: <span className={`font-mono ${linkedSite.status === "published" ? "text-green-700" : "text-amber-700"}`}>{linkedSite.status}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Link to={`/websites/${linkedSite.id}/edit`}
                    className="text-xs px-2 py-1.5 rounded bg-black text-white text-center hover:opacity-90"
                    data-testid="business-edit-website-manage">Manage website →</Link>
                  {linkedSite.status === "published" && (
                    <a href={`/sites/${linkedSite.slug}`} target="_blank" rel="noreferrer"
                       className="text-xs px-2 py-1.5 rounded border text-center hover:bg-black/5"
                       style={{ borderColor: "var(--wofy-border)" }}
                       data-testid="business-edit-website-view">View live →</a>
                  )}
                </div>
              </div>
            ) : (
              <Link to={`/websites/new?source_type=business&source_id=${biz.id}${suggestedTemplate(biz) ? `&template=${suggestedTemplate(biz)}` : ""}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors mt-4 border"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="business-edit-website-cta">
                <span className="text-xs">✨ Get an Instant Website →</span>
              </Link>
            )
          )}
        </nav>

        {/* Panels */}
        <div className="space-y-4 min-w-0">
          {tab === "basics" && (
            <div className="wofy-card !p-6 space-y-4">
              <Field label="Business name *"><input value={biz.name || ""} onChange={(e) => set({ name: e.target.value })} className="wofy-input" data-testid="edit-name" /></Field>
              <Field label="Short description">
                <textarea rows={4} value={biz.description || ""} onChange={(e) => set({ description: e.target.value })} className="wofy-input" data-testid="edit-description" placeholder="One or two paragraphs about who you are, what you do, and who you serve." />
              </Field>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Industry">
                  <select value={biz.industry || ""} onChange={(e) => set({ industry: e.target.value })} className="wofy-input" data-testid="edit-industry">
                    <option value="">— select —</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Founded (year)">
                  <input type="number" min={1500} max={new Date().getFullYear()} value={biz.founded_year || ""} onChange={(e) => set({ founded_year: e.target.value })} className="wofy-input" data-testid="edit-founded" placeholder="e.g. 1962" />
                </Field>
              </div>
              <Field label="Logo URL (paste an image link for now — upload UI ships next session)">
                <input value={biz.logo_url || ""} onChange={(e) => set({ logo_url: e.target.value })} className="wofy-input" data-testid="edit-logo-url" placeholder="https://…" />
              </Field>
              <Field label="Hero banner image URL">
                <input value={biz.hero_image_url || ""} onChange={(e) => set({ hero_image_url: e.target.value })} className="wofy-input" data-testid="edit-hero-url" placeholder="https://…" />
              </Field>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={biz.is_public !== false} onChange={(e) => set({ is_public: e.target.checked })} data-testid="edit-is-public" />
                Public listing (uncheck to hide from the directory)
              </label>
            </div>
          )}

          {tab === "contact" && (
            <div className="wofy-card !p-6 space-y-4">
              <Field label="Contact person"><input value={biz.contact_person || ""} onChange={(e) => set({ contact_person: e.target.value })} className="wofy-input" data-testid="edit-contact-person" /></Field>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Business email"><input type="email" value={biz.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} className="wofy-input" data-testid="edit-contact-email" /></Field>
                <Field label="Business phone"><input value={biz.contact_phone || ""} onChange={(e) => set({ contact_phone: e.target.value })} className="wofy-input" data-testid="edit-contact-phone" placeholder="+91 484 555 0100" /></Field>
              </div>
              <Field label="Address"><input value={biz.address || ""} onChange={(e) => set({ address: e.target.value })} className="wofy-input" data-testid="edit-address" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="City"><input value={biz.city || ""} onChange={(e) => set({ city: e.target.value })} className="wofy-input" data-testid="edit-city" /></Field>
                <Field label="State"><input value={biz.state || ""} onChange={(e) => set({ state: e.target.value })} className="wofy-input" data-testid="edit-state" /></Field>
                <Field label="Country"><input value={biz.country || ""} onChange={(e) => set({ country: e.target.value })} className="wofy-input" data-testid="edit-country" /></Field>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2"><Clock className="w-3 h-3 inline mr-1" /> Business hours</div>
                <div className="space-y-1.5">
                  {DAYS.map((d) => (
                    <div key={d} className="grid grid-cols-[80px_1fr] items-center gap-2">
                      <span className="text-xs">{d}</span>
                      <input value={biz.hours?.[d] || ""} onChange={(e) => setHours(d, e.target.value)} className="wofy-input !py-1 text-sm" data-testid={`edit-hours-${d}`} placeholder="e.g. 9am–6pm  or  Closed" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "social" && (
            <div className="wofy-card !p-6 space-y-4">
              <Field label="Website"><input value={biz.website || ""} onChange={(e) => set({ website: e.target.value })} className="wofy-input" data-testid="edit-website" placeholder="https://…" /></Field>
              {["twitter", "instagram", "facebook", "linkedin", "youtube", "whatsapp"].map((k) => (
                <Field key={k} label={k[0].toUpperCase() + k.slice(1) + (k === "whatsapp" ? " (link)" : " URL")}>
                  <input value={biz.social?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} className="wofy-input" data-testid={`edit-social-${k}`} placeholder={k === "whatsapp" ? "https://wa.me/…" : "https://…"} />
                </Field>
              ))}
            </div>
          )}

          {tab === "team" && <TeamPanel biz={biz} isOwner={isOwner} onChange={load} />}

          {tab === "pages" && <CustomPagesPanel biz={biz} onChange={load} />}

          {tab === "import" && <ImportWebsitePanel biz={biz} onChange={load} />}

          {tab === "ai" && <AiFillPanel biz={biz} isOwner={isOwner} onApply={(p) => { set(p); setTab("basics"); }} />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">{label}</div>
      {children}
    </label>
  );
}

// Pick a sensible default template for the website builder based on industry.
function suggestedTemplate(biz) {
  const ind = (biz?.industry || "").toLowerCase();
  if (ind === "restaurant") return "restaurant";
  if (ind === "retail")     return "grocery";
  if (ind === "hotel")      return "restaurant";
  if (ind === "education" || ind === "school") return "educational";
  return null;
}

function TeamPanel({ biz, isOwner, onChange }) {
  const [team, setTeam] = useState({ members: [], pending_invites: [] });
  const [loading, setLoading] = useState(true);
  const [inviteId, setInviteId] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/businesses/${biz.id}/team`);
      setTeam(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load team.");
    } finally { setLoading(false); }
  }, [biz.id]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!inviteId.trim()) return toast.error("Enter an email or phone.");
    setBusy(true);
    try {
      const { data } = await http.post(`/businesses/${biz.id}/invite`, { identifier: inviteId.trim(), role: inviteRole });
      if (data.already_member) toast.info("Already a team member.");
      else if (data.matched)   toast.success(`Added ${data.name || inviteId} as ${data.role}.`);
      else                     toast.success(`Invite queued for ${data.identifier}. They'll be added on first sign-in.`);
      setInviteId(""); load(); onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed.");
    } finally { setBusy(false); }
  };

  const removeMember = async (uid) => {
    if (!confirm("Remove this team member?")) return;
    try {
      await http.delete(`/businesses/${biz.id}/members/${uid}`);
      toast.success("Removed.");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed."); }
  };

  const changeRole = async (uid, role) => {
    try {
      await http.patch(`/businesses/${biz.id}/members/${uid}/role`, { role });
      toast.success("Role updated.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Role change failed."); }
  };

  if (loading) return <div className="wofy-card !p-6 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-60" /></div>;

  return (
    <div className="space-y-4">
      {isOwner && (
        <div className="wofy-card !p-4" data-testid="team-invite-form">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2"><UserPlus className="w-3 h-3 inline mr-1" /> Invite team member</div>
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={inviteId} onChange={(e) => setInviteId(e.target.value)}
              placeholder="Email or phone (+15551234567)"
              className="wofy-input flex-1"
              data-testid="team-invite-identifier"
            />
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="wofy-input md:w-40" data-testid="team-invite-role">
              <option value="editor">Editor</option>
              <option value="manager">Manager</option>
            </select>
            <button onClick={invite} disabled={busy} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="team-invite-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Invite
            </button>
          </div>
          <p className="text-[10px] opacity-60 mt-2">Managers can edit the profile; Editors can add/edit content but not the profile. Only the owner can add or remove team members.</p>
        </div>
      )}

      <div className="wofy-card !p-4">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2"><Users className="w-3 h-3 inline mr-1" /> Team ({team.members.length})</div>
        <ul className="divide-y" style={{ borderColor: "var(--wofy-border)" }}>
          {team.members.map((m) => (
            <li key={m.id} className="py-2 flex items-center gap-3" data-testid={`team-member-${m.id}`}>
              <div className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center text-xs font-medium">
                {(m.name || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{m.name || "(no name)"}</div>
                <div className="text-xs opacity-70 truncate">{m.email}</div>
              </div>
              {isOwner && m.role !== "owner" ? (
                <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} className="text-xs px-2 py-1 rounded border bg-white" style={{ borderColor: "var(--wofy-border)" }} data-testid={`team-member-role-${m.id}`}>
                  <option value="editor">Editor</option>
                  <option value="manager">Manager</option>
                </select>
              ) : (
                <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{m.role}</span>
              )}
              {isOwner && m.role !== "owner" && (
                <button onClick={() => removeMember(m.id)} className="text-xs opacity-60 hover:opacity-100" data-testid={`team-member-remove-${m.id}`} title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {team.pending_invites?.length > 0 && (
        <div className="wofy-card !p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Pending invites</div>
          <ul className="space-y-1">
            {team.pending_invites.map((inv) => (
              <li key={inv.id} className="text-sm flex items-center justify-between" data-testid={`pending-invite-${inv.id}`}>
                <span>{inv.identifier} <span className="text-[10px] font-mono opacity-60 ml-1">{inv.role.toUpperCase()}</span></span>
                <span className="text-[10px] opacity-60">waiting for sign-in</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isOwner && (
        <BusinessMagicLinkInvite businessId={biz.id} businessName={biz.name} />
      )}
    </div>
  );
}

function AiFillPanel({ biz, isOwner, onApply }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(biz.ai_draft || null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/businesses/${biz.id}/ai-fill`);
      setDraft(data.draft);
      toast.success("Draft ready — review and apply below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed. Try again.");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!draft) return;
    // We push the AI draft into the parent form (Basics tab) as the new
    // description + adds a small "mission/vision" note. The heavier
    // FAQ/social copy stays on the record for future tabs to consume.
    onApply({
      description: [draft.about_us, draft.mission ? `\n\nMission — ${draft.mission}` : "", draft.vision ? `\nVision — ${draft.vision}` : ""].join(""),
    });
    toast.success("Applied to the Basics tab — remember to Save.");
  };

  return (
    <div className="space-y-4">
      <div className="wofy-card !p-6">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl">AI-fill my business</div>
            <p className="text-sm opacity-70 mt-1">
              One click drafts your About Us, Mission, Vision, Values, Founder Story, 5 FAQs and 3 social posts.
              We use your name, industry, location and founded year as the seed. You can review before applying.
            </p>
            {isOwner && (
              <button onClick={run} disabled={busy} className="wofy-btn-primary mt-3 inline-flex items-center gap-2" data-testid="ai-fill-run">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {draft ? "Re-draft" : "Draft with AI"}
              </button>
            )}
          </div>
        </div>
      </div>

      {draft && (
        <>
          <PreviewCard title="About us" body={draft.about_us} />
          <div className="grid md:grid-cols-2 gap-3">
            <PreviewCard title="Mission" body={draft.mission} />
            <PreviewCard title="Vision"  body={draft.vision} />
          </div>
          {draft.values?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="overline mb-2">Values</div>
              <div className="flex flex-wrap gap-1.5">
                {draft.values.map((v, i) => <span key={i} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{v}</span>)}
              </div>
            </div>
          )}
          <PreviewCard title="Founder story" body={draft.founder_story} />
          {draft.faqs?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="overline mb-2">FAQs (5)</div>
              <ul className="space-y-3 text-sm">
                {draft.faqs.map((f, i) => (
                  <li key={i}>
                    <div className="font-medium">{f.question}</div>
                    <div className="opacity-80 mt-0.5">{f.answer}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {draft.social_posts?.length > 0 && (
            <div className="wofy-card !p-4">
              <div className="overline mb-2">Social posts (3)</div>
              <ul className="space-y-2 text-sm">
                {draft.social_posts.map((p, i) => <li key={i} className="p-2 rounded border" style={{ borderColor: "var(--wofy-border)" }}>{p}</li>)}
              </ul>
            </div>
          )}
          <div className="text-center">
            <button onClick={apply} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="ai-fill-apply">
              <Save className="w-4 h-4" /> Apply About / Mission / Vision to Basics
            </button>
            <p className="text-[10px] opacity-60 mt-2">FAQs, values, founder story and social posts stay saved on the business for future tabs.</p>
          </div>
        </>
      )}
    </div>
  );
}
